import {
  Button,
  Icon,
  OutlinedSelect,
  OutlinedSelectOptionType,
  TextOutlinedInput,
  addToast,
} from '@octano/global-ui';
import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Col, Form, FormGroup, Label, Row } from 'reactstrap';
import DisplayError from '../../../components/info/DisplayError';
import { useValidations } from '../../../hooks/useValidations';
import { TranslationsKeys } from '../../../locales/translations';
import {
  CreditLiquidationFilterFormFields,
  LiquidationVersionSummary,
} from '../types';
import { useCreditLiquidationFilterLoader } from './CreditLiquidationFilterLoader';
import { useCreditLiquidationDataLoader } from './CreditLiquidationDataLoader';
import {
  getCreditLiquidationPreview,
  executeCreditLiquidation,
  downloadLiquidationExcel,
  getLatestLiquidationVersion,
} from '../api';
import { LiquidationHistoryModal } from './LiquidationHistoryModal';

export const prefix = (key: string) => `filter.${key}`;

export const CreditLiquidationFilter = () => {
  const { t } = useTranslation(TranslationsKeys.CREDIT_LIQUIDATION);
  const { msgValidations } = useValidations();
  const { data } = useCreditLiquidationFilterLoader();
  const { updateQuery, query } = useCreditLiquidationDataLoader();
  const [isLiquidating, setIsLiquidating] = useState(false);
  const [
    latestVersion,
    setLatestVersion,
  ] = useState<LiquidationVersionSummary | null>(null);
  const [isHistoryModalOpen, setIsHistoryModalOpen] = useState(false);
  const {
    periods,
    schools,
    studyPlans,
    campuses,
    schedules,
    modalities,
  } = data!;

  const {
    control,
    handleSubmit,
    formState,
    reset,
    watch,
  } = useForm<CreditLiquidationFilterFormFields>({
    defaultValues: {
      period: null,
      studentId: '',
      school: null,
      studyPlan: null,
      campus: null,
      schedule: null,
      modality: null,
    },
  });

  const optionsPeriods = useMemo<OutlinedSelectOptionType[]>(() => {
    return periods.map((p) => ({
      value: p.id,
      label: p.name,
    }));
  }, [periods]);

  const optionsSchools = useMemo<OutlinedSelectOptionType[]>(() => {
    return schools.map((s) => ({
      value: s.id,
      label: s.name,
    }));
  }, [schools]);

  const optionsStudyPlans = useMemo<OutlinedSelectOptionType[]>(() => {
    return studyPlans.map((sp) => ({
      value: sp.id,
      label: sp.name,
    }));
  }, [studyPlans]);

  const optionsCampuses = useMemo<OutlinedSelectOptionType[]>(() => {
    return campuses.map((c) => ({
      value: c.id,
      label: c.name,
    }));
  }, [campuses]);

  const optionsSchedules = useMemo<OutlinedSelectOptionType[]>(() => {
    return schedules.map((s) => ({
      value: s.id,
      label: s.name,
    }));
  }, [schedules]);

  const optionsModalities = useMemo<OutlinedSelectOptionType[]>(() => {
    return modalities.map((m) => ({
      value: m.id,
      label: m.name,
    }));
  }, [modalities]);

  const selectedPeriod = watch('period');

  useEffect(() => {
    if (selectedPeriod?.value) {
      getLatestLiquidationVersion(Number(selectedPeriod.value))
        .then((response) => {
          setLatestVersion(response.data ?? null);
        })
        .catch(() => {
          setLatestVersion(null);
        });
    } else {
      setLatestVersion(null);
    }
  }, [selectedPeriod]);

  if (Array.isArray(optionsPeriods) && optionsPeriods.length === 0) {
    return <DisplayError insideCard textBody={t(prefix('noPeriods'))} />;
  }

  const handleSearch = async (formData: CreditLiquidationFilterFormFields) => {
    const periodId = Number(formData.period?.value);
    const filterParams = {
      periodId,
      studentId: formData.studentId || undefined,
      schoolId: formData.school?.value
        ? Number(formData.school.value)
        : undefined,
      studyPlanId: formData.studyPlan?.value
        ? Number(formData.studyPlan.value)
        : undefined,
      campusId: formData.campus?.value
        ? Number(formData.campus.value)
        : undefined,
      scheduleId: formData.schedule?.value
        ? Number(formData.schedule.value)
        : undefined,
      modalityId: formData.modality?.value
        ? Number(formData.modality.value)
        : undefined,
    };

    setIsLiquidating(true);

    try {
      // 1. Obtener todos los estudiantes que coinciden con los filtros
      const previewResponse = await getCreditLiquidationPreview({
        ...filterParams,
        page: 0,
        items_per_page: 10000, // Obtener todos para liquidar
      });

      if (
        !previewResponse.data?.data ||
        previewResponse.data.data.length === 0
      ) {
        addToast({
          icon: 'information',
          color: 'warning',
          text: t('execute.noStudents'),
        });
        setIsLiquidating(false);
        return;
      }

      // 2. Extraer los IDs de los estudiantes
      const studyPlanEnrollmentIds = previewResponse.data.data.map(
        (student) => student.studyPlanEnrollmentId,
      );

      // 3. Ejecutar la liquidación (guardar en BD)
      const executeResponse = await executeCreditLiquidation({
        periodId,
        studyPlanEnrollmentIds,
        schoolId: filterParams.schoolId,
        studyPlanId: filterParams.studyPlanId,
        campusId: filterParams.campusId,
        scheduleId: filterParams.scheduleId,
        modalityId: filterParams.modalityId,
      });

      if (executeResponse.data?.success) {
        addToast({
          icon: 'success',
          color: 'success',
          text: t('execute.success', {
            count: executeResponse.data.processedCount,
          }),
        });
      } else if (executeResponse.data?.errors?.length) {
        addToast({
          icon: 'warning',
          color: 'warning',
          text: t('execute.partialSuccess', {
            processed: executeResponse.data.processedCount,
            errors: executeResponse.data.errors.length,
          }),
        });
      }

      // 4. Actualizar la vista con los resultados paginados
      updateQuery({
        ...query,
        ...filterParams,
        page: 1,
        items_per_page: 10,
      });
    } catch (error) {
      console.error('Error executing liquidation:', error);
      addToast({
        icon: 'error',
        color: 'danger',
        text: t('execute.error'),
      });
    } finally {
      setIsLiquidating(false);
    }
  };

  const handleClear = () => {
    reset();
    updateQuery({ page: 0, items_per_page: 10 });
  };

  const handleDownloadList = async () => {
    const periodId = watch('period')?.value;
    if (!periodId) {
      addToast({
        icon: 'warning',
        color: 'warning',
        text: t(prefix('selectPeriodFirst')),
      });
      return;
    }
    try {
      await downloadLiquidationExcel(Number(periodId));
    } catch (error) {
      addToast({
        icon: 'error',
        color: 'danger',
        text: t(prefix('downloadError')),
      });
    }
  };

  return (
    <Form
      onSubmit={handleSubmit(handleSearch)}
      className="g-table-container mt-2 pb-4 pt-4"
    >
      {/* Primera fila: Periodo, ID, Plantel, Programa */}
      <Row>
        <Col lg={3} className="pl-0">
          <FormGroup>
            <Label className="text-muted small fw-bold mb-1">
              {t(prefix('periodLabel'))}
            </Label>
            <OutlinedSelect
              name="period"
              control={control}
              label={''}
              placeholder={t(prefix('selectPeriod'))}
              options={optionsPeriods}
              rules={{ required: msgValidations.required }}
            />
          </FormGroup>
        </Col>
        <Col lg={3} className="pl-0">
          <FormGroup>
            <Label className="text-muted small fw-bold mb-1">
              {t(prefix('idLabel'))}
            </Label>
            <TextOutlinedInput
              name="studentId"
              control={control}
              label={''}
              placeholder={t(prefix('searchById'))}
            />
          </FormGroup>
        </Col>
        <Col lg={3} className="pl-0">
          <FormGroup>
            <Label className="text-muted small fw-bold mb-1">
              {t(prefix('schoolLabel'))}
            </Label>
            <OutlinedSelect
              name="school"
              control={control}
              label={''}
              placeholder={t(prefix('selectSchool'))}
              isClearable
              options={optionsSchools}
            />
          </FormGroup>
        </Col>
        <Col lg={3} className="pl-0">
          <FormGroup>
            <Label className="text-muted small fw-bold mb-1">
              {t(prefix('programLabel'))}
            </Label>
            <OutlinedSelect
              name="studyPlan"
              control={control}
              label={''}
              placeholder={t(prefix('selectStudyPlan'))}
              isClearable
              options={optionsStudyPlans}
            />
          </FormGroup>
        </Col>
      </Row>
      {/* Segunda fila: Sede, Modalidad, Jornada, Botones */}
      <Row className="mt-3">
        <Col lg={3} className="pl-0">
          <FormGroup>
            <Label className="text-muted small fw-bold mb-1">
              {t(prefix('campusLabel'))}
            </Label>
            <OutlinedSelect
              name="campus"
              control={control}
              label={''}
              placeholder={t(prefix('selectCampus'))}
              isClearable
              options={optionsCampuses}
            />
          </FormGroup>
        </Col>
        <Col lg={3} className="pl-0">
          <FormGroup>
            <Label className="text-muted small fw-bold mb-1">
              {t(prefix('modalityLabel'))}
            </Label>
            <OutlinedSelect
              name="modality"
              control={control}
              label={''}
              placeholder={t(prefix('selectModality'))}
              isClearable
              options={optionsModalities}
            />
          </FormGroup>
        </Col>
        <Col lg={3} className="pl-0">
          <FormGroup>
            <Label className="text-muted small fw-bold mb-1">
              {t(prefix('scheduleLabel'))}
            </Label>
            <OutlinedSelect
              name="schedule"
              control={control}
              label={''}
              placeholder={t(prefix('selectSchedule'))}
              isClearable
              options={optionsSchedules}
            />
          </FormGroup>
        </Col>
        {/* Botones en la misma fila */}
        <Col
          lg={3}
          className="pl-0 d-flex align-items-end pb-3"
          style={{ gap: '12px' }}
        >
          <Button
            text={t(prefix('liquidate'))}
            type="submit"
            size="md"
            loading={isLiquidating || formState.isSubmitting}
            disabled={isLiquidating || formState.isSubmitting}
            style={{ whiteSpace: 'nowrap' }}
          />
          <Button
            text={t(prefix('clear'))}
            type="button"
            size="md"
            outlined
            onClick={handleClear}
          />
        </Col>
      </Row>
      {/* Tercera fila: Historial y Descargar */}
      <Row className="mt-4 justify-content-between align-items-center">
        <Col
          md="auto"
          className="pl-0 d-flex align-items-center"
          style={{ gap: '16px' }}
        >
          <span className="text-muted" style={{ fontSize: '14px' }}>
            {t(prefix('lastGenerated'))}:{' '}
            <strong style={{ color: '#333' }}>
              {latestVersion?.executedAt
                ? new Date(latestVersion.executedAt).toLocaleDateString(
                    'es-CL',
                    {
                      day: '2-digit',
                      month: '2-digit',
                      year: '2-digit',
                    },
                  )
                : '--/--/--'}
            </strong>
          </span>
          <p
            className="text-primary d-flex align-items-center text-decoration-none m-0 cursor-pointer"
            style={{ gap: '6px', fontSize: '14px' }}
            onClick={(e) => {
              e.preventDefault();
              if (selectedPeriod?.value) {
                setIsHistoryModalOpen(true);
              }
            }}
          >
            <Icon name="classes_history" size="14px" />
            {t(prefix('viewHistory'))}
          </p>
        </Col>
        <Col md="auto" className="pl-0">
          <Button
            text={t(prefix('downloadList'))}
            type="button"
            size="md"
            outlined
            onClick={handleDownloadList}
            icon="download"
          />
        </Col>
      </Row>
      <LiquidationHistoryModal
        isOpen={isHistoryModalOpen}
        onClose={() => setIsHistoryModalOpen(false)}
        periodId={selectedPeriod?.value ? Number(selectedPeriod.value) : null}
      />
    </Form>
  );
};
